import React, { useContext, useState, useRef, useEffect } from "react";
import {
  BrainCircuit,
  Clock,
  Database,
  FileText,
  GitBranch,
  Package,
  Headset,
  History,
  Home,
  LayoutDashboard,
  ListChecks,
  Menu,
  Network,
  Settings,
  ShieldUser,
  TrendingUp,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { hasScreenAccess, getStoredPermissions } from "../../utils/permissions";
import ModalCloseButton from "../common/ModalCloseButton";
import CommonTooltip from "../common/CommonTooltip";

// Side-menu hint: opens beside the icon (CommonTooltip mirrors left/right for Arabic/Urdu and
// flips to the other side when there is no room).
const Tooltip = ({ children, text }) => (
  <CommonTooltip
    text={text}
    position="right"
    disableDefaultDescription
    className="relative inline-block"
    tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
    fitContent
    hoverOnly
  >
    {children}
  </CommonTooltip>
);

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const SHELL_BG = "bg-gradient-to-b from-[#F5FAFF] via-[#EEF6FF] to-[#E6F2FF]";
const SHELL_BORDER = "border border-[#D3E7FB]";
const SHELL_SHADOW = "shadow-[0_10px_30px_rgba(35,109,180,0.14)]";
const ITEM_BASE =
  "group relative flex cursor-pointer transition-all duration-200 ease-out";
const ITEM_EXPANDED = "mx-2 my-1.5 min-h-[44px] items-center gap-3 rounded-xl px-3 py-2 text-sm";
const ITEM_COLLAPSED = "my-1 h-10 w-10 items-center justify-center rounded-xl";
const ITEM_ACTIVE =
  "bg-gradient-to-r from-[#236DB4] to-[#2A7FCD] text-white hover:text-white shadow-[0_8px_18px_rgba(35,109,180,0.26)]";
const ITEM_IDLE =
  "text-[#111827] hover:bg-white/95 hover:text-[#236DB4] hover:shadow-[0_6px_16px_rgba(35,109,180,0.12)]";

const DashboardMenus = ({ setShowNames = () => { }, showNames = false }) => {
  const { theme, langMode, userType, currentLocationRef } = useContext(AppContext);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [screenSize, setScreenSize] = useState(false);
  const storedPermissions = getStoredPermissions();
  const fullAccess = storedPermissions?.fullAccess ?? false;
  const hasAccess = (screenCode) => fullAccess || hasScreenAccess(screenCode, "canView");
  const userDetail = JSON.parse(localStorage.getItem("userDetails")) || {};

  useEffect(() => {
    const updateHeightScreenSize = () => {
      if (window.innerWidth <= 1213) {
        setScreenSize(true);
      } else if (window.innerWidth > 1213) {
        setScreenSize(false);
      } else {
        setScreenSize(false);
      }
    };
    updateHeightScreenSize();
    window.addEventListener('resize', updateHeightScreenSize);
    return () => window.removeEventListener('resize', updateHeightScreenSize);
  }, []);

  const menus = [
    {
      icon: (
        <ListChecks
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/maptask",
      relatedPaths: ["/maptask", "/task-manage"],
      name: translations[langMode].task,
      tooltip: translations[langMode].tasktooltip,
      screenCode: "TASK_MANAGEMENT",
      isPermission: hasAccess("TASK_MANAGEMENT"),
    },
    {
      icon: (
        <LayoutDashboard
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/dashboard",
      relatedPaths: ["/dashboard", "/activty-dashboard", "/powerbi-dashboard"],
      name: translations[langMode].dashboard,
      tooltip: translations[langMode].dashboardtolltip,
      screenCode: "ACTIVITY_DASHBOARD",
      isPermission: hasAccess("ACTIVITY_DASHBOARD"),
    },
    {
      icon: (
        <Users
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/all-users",
      relatedPaths: ["/all-users"],
      name: translations[langMode].userlist,
      tooltip: translations[langMode].usertooltip,
      screenCode: "USER_MANAGEMENT",
      isPermission: hasAccess("USER_MANAGEMENT"),
    },
    {
      icon: (
        <UserCog
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/role-management",
      relatedPaths: ["/role-management"],
      name: translations[langMode].roleManagement,
      tooltip: translations[langMode].roleManagementtooltip,
      screenCode: "ROLE_MANAGEMENT",
      isPermission: hasAccess("ROLE_MANAGEMENT"),
    },
    {
      icon: (
        <Settings
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/settings",
      relatedPaths: ["/settings"],
      name: translations[langMode].setting,
      tooltip: translations[langMode].setting,
      screenCode: "SETTINGS",
      isPermission: hasAccess("SETTINGS"),
    },
    {
      icon: (
        <Database
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/configuration",
      relatedPaths: ["/configuration"],
      name: translations[langMode].config,
      tooltip: translations[langMode].configtooltip,
      screenCode: "CONFIGURATION",
      isPermission: hasAccess("CONFIGURATION") || hasAccess("ASSIGNMENT_RULES") || hasAccess("SPECIAL_ASSIGNMENT_RULES"),
    },
    {
      icon: (
        <History
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/export-report-and-data",
      relatedPaths: ["/export-report-and-data"],
      name: translations[langMode].accReportsCenter,
      tooltip: translations[langMode].accReportsCentertooltip,
      screenCode: "ACCOUNT_HISTORY",
      isPermission: hasAccess("ACCOUNT_HISTORY"),
    },
    {
      icon: (
        <GitBranch
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/workflow",
      relatedPaths: ["/workflow"],
      name: translations[langMode].workflowe,
      tooltip: translations[langMode].workflowtooltip,
      screenCode: "WORKFLOW",
      isPermission: hasAccess("WORKFLOW"),
    },

    {
      icon: (
        <Clock
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/worklog",
      relatedPaths: ["/worklog", "/pdf-generator", "/pdf-export-history"],
      name: translations[langMode].worklog,
      tooltip: translations[langMode].worklogtooltip,
      screenCode: "WORKLOG",
      isPermission: hasAccess("WORKLOG"),
    },
    {
      icon: (
        <ShieldUser
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/security",
      relatedPaths: ["/security", "/security/protection-jobs"],
      name: translations[langMode].security,
      tooltip: translations[langMode].securitytooltip,
      screenCode: "ACCOUNT_PROTECTION",
      isPermission: hasAccess("ACCOUNT_PROTECTION"),
    },
    {
      icon: (
        <TrendingUp
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/stock-managment",
      relatedPaths: ["/stock-managment"],
      name: translations[langMode].stock,
      tooltip: translations[langMode].stocktoooltip,
      screenCode: "METER_INVENTORY",
      isPermission: hasAccess("METER_INVENTORY"),
    },
    {
      icon: (
        <Package
          className={` Â ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/inventory-management",
      relatedPaths: [
        "/inventory-management",
        "/inventory-management/stores",
        "/inventory-management/add",
        "/inventory-management/import",
      ],
      name: translations[langMode].inventoryManagement,
      tooltip: translations[langMode].inventoryManagementTooltip,
      // The backend registers this screen as INVENTORY_MANAGEMENT; METER_INVENTORY is kept
      // so roles created before the rename keep their access.
      screenCode: "INVENTORY_MANAGEMENT",
      isPermission:
        hasAccess("INVENTORY_MANAGEMENT") ||
        hasAccess("METER_INVENTORY"),
    },
    {
      icon: (
        <FileText
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/reports",
      relatedPaths: ["/reports"],
      name: translations[langMode].report,
      tooltip: translations[langMode].reportooltip,
      screenCode: "REPORTS",
      isPermission: hasAccess("REPORTS"),
    },
    {
      icon: (
        <Network
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/organization",
      relatedPaths: ["/organization"],
      name: translations[langMode].org,
      tooltip: translations[langMode].orgtooltip,
      screenCode: "DEPARTMENT",
      isPermission: hasAccess("DEPARTMENT") || hasAccess("PROJECT"),
    },
    {
      icon: <Headset size={20} className={theme} />,
      link: "/customer-support",
      relatedPaths: ["/customer-support"],
      name: translations[langMode].support,
      tooltip: translations[langMode].support,
      screenCode: "CUSTOMER_SUPPORT",
      isPermission: hasAccess("CUSTOMER_SUPPORT"),
    },
    {
      icon: <BrainCircuit size={20} className={theme} />,
      link: "/app-update-config",
      relatedPaths: ["/app-update-config"],
      name: translations[langMode].appUpdateConfig,
      tooltip: translations[langMode].appUpdateConfigTooltip,
      screenCode: "APP_UPDATE_CONFIG",
      isPermission: userType === "super_admin",
    },

  ];

  const customerSupport = [
    {
      icon: <Headset size={20} className={theme} />,
      link: "/customer-support",
      relatedPaths: ["/customer-support"],
      name: translations[langMode].support,
      tooltip: translations[langMode].supporttooltip,
      isPermission: hasAccess("CUSTOMER_SUPPORT"),
    },
    {
      icon: (
        <History
          className={`  ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
          size={20}
        />
      ),
      link: "/export-report-and-data",
      relatedPaths: ["/export-report-and-data"],
      name: translations[langMode].accReportsCenter,
      tooltip: translations[langMode].accReportsCentertooltip,
      screenCode: "ACCOUNT_HISTORY",
      isPermission: hasAccess("ACCOUNT_HISTORY"),
    },
  ];

  // if (userType === "super_admin") {
  //   menus.push(
  //     {
  //       icon: (
  //         <SiAwsorganizations
  //           className={`  ${theme === "dark"
  //             ? "dark-mode"
  //             : theme === "high-contrast"
  //               ? "high-contrast"
  //               : ""
  //             }`}
  //           size={20}
  //         />
  //       ),
  //       link: "/organization",
  //       relatedPaths: ["/organization"],
  //       name: translations[langMode].org,
  //       tooltip: translations[langMode].orgtooltip,
  //     },
  //   );
  // }

  const handleNavigation = (nextPath, onClickHandler) => {
    if (onClickHandler) {
      onClickHandler();
      return;
    }

    const prevPath = currentLocationRef.current?.pathname || location.pathname;
    const isBlocked =
      prevPath?.startsWith('/update-complete-task') &&
      prevPath !== nextPath;

    if (isBlocked) {
      window.dispatchEvent(new CustomEvent("blockNavigation", {
        detail: { nextPath }
      }));
      return;
    }

    currentLocationRef.current = { pathname: nextPath };
    navigate(nextPath);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setShowNames(false);
      }
    };
    if (showNames) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNames, setShowNames]);

  let filteredMenus;
  if (userType === "IMS_ADMIN" || userType === "IMS_INTERNAL_PORTAL_USER") {
    filteredMenus = menus.filter(
      (menu) => menu.link === "/all-users" || menu.link === "/workflow-crm"
    );
  // } else if (userType === "office_user" && hasAccess("CUSTOMER_SUPPORT")) {
  //   // filteredMenus = customerSupport.filter((m) => m.isPermission);
  //   // filteredMenus = customerSupport;
  //   filteredMenus = customerSupport.filter(
  //     (menu) => menu.link !== "/dashboard",
  //   );
  } else if (userType === "super_admin") {
    filteredMenus = menus;
  } else {
    filteredMenus = menus.filter((menu) => menu.isPermission === true);
  }

  const firstVisibleMenuIndex = filteredMenus.findIndex(
    (menu) => menu.isPermission !== false
  );
  const homeIconForFirstItem = (
    <Home
      className={` ${theme === "dark"
        ? "dark-mode"
        : theme === "high-contrast"
          ? "high-contrast"
          : ""
        }`}
      size={20}
    />
  );

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location]);

  const isMobileCollapsed = screenSize && !isSidebarOpen && !showNames;

  if (screenSize) {
    return (
      <>
        <button
          type="button"
          aria-label="Open dashboard menu"
          className={`flex h-10 w-10 items-center justify-center rounded-xl border border-transparent bg-transparent text-gray-700 shadow-none transition-all duration-200 hover:border-[#D3E7FB] hover:bg-white/80 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#236DB4]/40 ${theme === "dark" ? "dark-mode" : ""}`}
          onClick={() => setIsSidebarOpen(true)}
        >
          <LayoutDashboard size={20} className={`${theme === "dark" ? "dark-mode" : ""} text-gray-700`} />
        </button>

        {isSidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-[45] bg-black/40"
              onClick={() => setIsSidebarOpen(false)}
            />
            <aside
              ref={sidebarRef}
              className={`fixed left-0 top-0 z-[46] flex h-dvh w-[250px] flex-col overflow-hidden rounded-r-[20px] px-4 py-3 ${SHELL_BG} ${SHELL_BORDER} ${SHELL_SHADOW} ${theme === "dark"
                ? "dark-mode"
                : theme === "high-contrast"
                  ? "high-contrast"
                  : ""
                }`}
            >
              <div className="flex shrink-0 justify-end">
                <ModalCloseButton
                  onClick={() => setIsSidebarOpen(false)}
                  ariaLabel="Close dashboard menu"
                  className="h-8 w-8 rounded-xl bg-transparent border-transparent text-gray-700 hover:bg-white/80 hover:border-red-100 hover:shadow-sm"
                />
              </div>
              <div className="flex w-full shrink-0 items-center justify-center py-3">
                <Link to="/dashboard" onClick={() => setIsSidebarOpen(false)}>
                  <img
                    src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344904/Bluefield_x0jmp9.png"
                    alt="Logo"
                    className="h-8"
                  />
                </Link>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-3 custom-side-scrollbar">
                {filteredMenus.map((menu, index) => {
                  const isActive =
                    location.pathname === menu.link ||
                    menu.relatedPaths.includes(location.pathname);
                  const menuIcon =
                    index === firstVisibleMenuIndex ? homeIconForFirstItem : menu.icon;
                  return (
                    <div
                      key={index}
                      className={`relative w-full text-[#111827] ${menu.isPermission === false ? "hidden" : ""}`}
                    >
                      <div
                        className={classNames(
                          "mx-0 my-1.5 flex min-h-[44px] cursor-pointer items-center gap-3 rounded-xl border border-transparent px-3 py-2 text-sm transition-all duration-200 ease-out",
                          isActive
                            ? `${ITEM_ACTIVE} border-[#236DB4]/10`
                            : `${ITEM_IDLE} hover:border-[#D3E7FB]`
                        )}
                        onClick={() => {
                          handleNavigation(menu.link, menu.onClick);
                          setIsSidebarOpen(false);
                        }}
                      >
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                          {menuIcon}
                        </span>
                        <span className="min-w-0 flex-1 text-[14px] font-medium leading-[18px]">
                          {menu.name}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </aside>
          </>
        )}
      </>
    );
  }

  return (

    <>
      {showNames && (
        <div className="pointer-events-none fixed inset-0 z-40 bg-black bg-opacity-5 transition-all duration-300" />
      )}
      {isSidebarOpen && (
        <div className="pointer-events-none fixed inset-0 bg-black bg-opacity-10 transition-all duration-300" />
      )}
      <div
        ref={sidebarRef}
        className={`relative
          ${isMobileCollapsed
            ? "h-auto w-auto overflow-visible rounded-none bg-transparent"
            : `h-full overflow-hidden rounded-[20px] ${SHELL_BG} ${SHELL_BORDER} ${SHELL_SHADOW}`}
          ${showNames
            ? "lg:w-[200px] opacity-100 h-full"
            : "lg:w-[80px] opacity-100 md:ps-0 sm:ps-0 ps-0"
          }
          ${isSidebarOpen
            ? "w-[200px] opacity-100 h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] p-[11px]"
            : isMobileCollapsed ? "opacity-100" : "w-0 opacity-100"}
          ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""}
          ${theme === "dark" && showNames
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
          }`}>
        {screenSize &&
          <div
            className={`relative flex items-center transition-all duration-300 ease-in-out
            ${showNames
                ? "lg:w-[200px] opacity-100 rounded-t-xl"
                : "lg:w-[80px] opacity-100"}
            ${isSidebarOpen
                ? "w-[200px] opacity-100 rounded-t-xl"
                : isMobileCollapsed ? "h-10 w-10 opacity-100" : "w-0 opacity-100"}
            ${theme === "dark"
                ? "dark-mode"
                : theme === "high-contrast"
                  ? "high-contrast"
                  : ""
              }`}
            style={{
              top: (isSidebarOpen || showNames) ? "0px" : "0px",
              justifyContent: (isSidebarOpen || showNames) ? "end" : "center",
              height: isMobileCollapsed ? "40px" : "30px",
              background: "transparent",


            }}>
            {isSidebarOpen ? (
              <ModalCloseButton
                onClick={() => setIsSidebarOpen(false)}
                ariaLabel="Close sidebar"
                className={`lg:hidden h-10 w-10 rounded-xl ${isSidebarOpen ? "absolute right-0" : ""} ${theme === "dark" ? "dark-mode" : ""}`}
              />
            ) : (
              <button
                className={`lg:hidden flex h-10 w-10 items-center justify-center rounded-xl text-gray-700 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#236DB4]/40 ${isMobileCollapsed ? "bg-transparent shadow-none hover:bg-transparent" : "hover:bg-white/85 hover:shadow-sm"} ${theme === "dark" ? "dark-mode" : ""}`}
                onClick={() => setIsSidebarOpen(true)}
              >
                <LayoutDashboard size={20} className={`${theme === "dark" ? "dark-mode" : ""} text-gray-700`} />
              </button>
            )}
          </div>}
        <div
          className={`relative z-30 flex h-full min-h-0 flex-col items-center overflow-hidden rounded-[20px] transition-all duration-300 ease-in-out
            ${theme === "dark"
              ? "dark-mode"
              : theme === "high-contrast"
                ? "high-contrast"
                : ""}
            ${showNames
              ? "w-full opacity-100"
              : "w-full opacity-100"}
            ${isSidebarOpen
              ? "w-[200px] opacity-100 py-[25px] px-[10px]"
              : "opacity-100 md:pointer-events-auto"}
              ${screenSize ? (!isSidebarOpen && !showNames) ? "hidden" : "" : ""}
                `}
                data-testid="dashboard-menus"
          >
          <div className={`flex w-full shrink-0 items-center justify-center ${(showNames || isSidebarOpen) ? "py-2 mt-2" : "py-2"}`}>
            <Link to="/dashboard">
              {(showNames || isSidebarOpen) ? (
                <img
                  src='https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344904/Bluefield_x0jmp9.png'
                  alt="Logo"
                  className="h-8 mb-3"
                />
              ) : (
                <img
                  src='https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344907/logo_iiqugo.png'
                  alt="Logo"
                  className={`h-10 ${theme === "dark" ? "dark-mode-img" : ""}`}
                />
              )}
            </Link>
          </div>
          <div className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-3 custom-side-scrollbar ${(showNames || isSidebarOpen) ? "w-full" : "flex w-full flex-col items-center"}`}>
            {!isSidebarOpen && !showNames && (
              <div className="relative flex w-full justify-center text-[#111827]">
                <div
                  onClick={() => setShowNames(true)}
                  className="my-1 flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl border border-transparent text-gray-700 transition-all duration-200 hover:border-[#D3E7FB] hover:bg-white/90 hover:text-blue-600 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#236DB4]/40"
                >
                  <Menu />
                </div>
              </div>
            )}
            {filteredMenus.map((menu, index) => {
              const isActive =
                location.pathname === menu.link ||
                menu.relatedPaths.includes(location.pathname);
              const menuIcon =
                index === firstVisibleMenuIndex ? homeIconForFirstItem : menu.icon;
              const isExpanded = showNames || isSidebarOpen;
              const menuItem = (
                <div
                  data-testid="dashboard-menu-item"
                className={classNames(
                    ITEM_BASE,
                    isExpanded
                      ? ITEM_EXPANDED
                      : ITEM_COLLAPSED,
                    isActive
                      ? ITEM_ACTIVE
                      : ITEM_IDLE
                  )}
                  onClick={() => handleNavigation(menu.link, menu.onClick)}
                >
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center ${isActive ? "text-white hover:text-white group-hover:text-white" : ""}`}>
                    {menuIcon}
                  </span>
                  {isExpanded && (
                    <span className="min-w-0 flex-1 text-[14px] font-medium leading-[18px]">
                      {menu.name}
                    </span>
                  )}
                </div>
              );
              return (
                <div
                  data-testid="dashboard-menu-item-container"
                key={index}
                  className={`relative text-[#111827] ${menu.isPermission === false ? "hidden" : ""} ${(!showNames && !isSidebarOpen) ? "flex w-full justify-center" : "w-full"}`}
                >
                  {(!showNames && !isSidebarOpen) ? (
                    <Tooltip text={menu.tooltip || menu.name}>{menuItem}</Tooltip>
                  ) : (
                    menuItem
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </div >
    </>
  );
};

export default DashboardMenus;
